import { Component, ElementRef, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output } from "@angular/core";
import { type TagPreset } from "src/app/shared/constants/tag-presets";
import { placeFixedPopover } from "../domains/fixed-popover";
import { parseTypedDate } from "../domains/typed-date";
import { onDocumentClickCapture } from "src/app/shared/ds/utils/listen-outside-angular";

interface CalendarDay {
  date: Date;
  day: number;
  isCurrentMonth: boolean;
  isToday: boolean;
  isSelected: boolean;
  isDisabled: boolean;
  isStart: boolean;
  isEnd: boolean;
  isInRange: boolean;
}

@Component({
  selector: "app-task-date-picker",
  templateUrl: "./task-date-picker.component.html",
  styleUrls: ["./task-date-picker.component.scss"],
})
export class TaskDatePickerComponent implements OnInit, OnDestroy {
  /** Date de fin (échéance ou fin de période) */
  @Input() date: Date = null;
  /** Date de début de période */
  @Input() plannedStartDate: Date = null;
  /** Placeholder quand vide */
  @Input() placeholder: string = "Date";
  /** Date minimum selectionnable */
  @Input() minDate: Date = null;
  /** Mode desactive */
  @Input() disabled: boolean = false;
  /** Afficher le bouton clear */
  @Input() showClear: boolean = true;
  /** Taille compacte */
  @Input() compact: boolean = false;
  /** Mode de sélection de date */
  @Input() dateMode: "none" | "deadline" | "range" = "none";

  /** Two-way binding date de fin */
  @Output() dateChange = new EventEmitter<Date | null>();
  /** Two-way binding date de début */
  @Output() plannedStartDateChange = new EventEmitter<Date | null>();
  /** Two-way binding mode date */
  @Output() dateModeChange = new EventEmitter<"none" | "deadline" | "range">();

  isOpen = false;
  currentMonth: Date;
  calendarDays: CalendarDay[] = [];
  weekDays = ["Lu", "Ma", "Me", "Je", "Ve", "Sa", "Di"];
  rangeStep: "start" | "end" = "start";
  /** Saisie clavier de la date (02.11.2026, 021126, 0211…) */
  typedDate = "";
  typedDateError = "";

  private today: Date;

  /** Clic à l'extérieur, en capture : le galet arrête la propagation des clics */
  private readonly stopOutsideClick = onDocumentClickCapture(
    () => this.isOpen,
    (event) => this.onDocumentClick(event),
  );

  constructor(private elementRef: ElementRef) {}

  ngOnInit(): void {
    this.today = new Date();
    this.today.setHours(0, 0, 0, 0);
    // Déduire le mode depuis les dates existantes si non fourni
    if (this.dateMode === "none") {
      if (this.plannedStartDate != null && this.date != null) this.dateMode = "range";
      else if (this.date != null) this.dateMode = "deadline";
    }
    if (this.dateMode === "range") {
      this.rangeStep = this.plannedStartDate && !this.date ? "end" : "start";
    }
    this.currentMonth = this.date
      ? new Date(this.date)
      : this.plannedStartDate
        ? new Date(this.plannedStartDate)
        : new Date();
    this.currentMonth.setDate(1);
    this.buildCalendar();
  }

  /** Label affiché dans le trigger */
  get displayLabel(): string {
    if (this.dateMode === "none" || (!this.date && !this.plannedStartDate)) return this.placeholder;

    if (this.dateMode === "range") {
      const start = this.plannedStartDate ? this.formatShortDate(this.plannedStartDate) : "?";
      const end = this.date ? this.formatShortDate(this.date) : "?";
      return `${start} → ${end}`;
    }

    // Mode deadline
    if (!this.date) return this.placeholder;

    const now = new Date();
    const dateValue = new Date(this.date);
    dateValue.setHours(0, 0, 0, 0);

    const todayDate = new Date(now);
    todayDate.setHours(0, 0, 0, 0);

    const tomorrowDate = new Date(todayDate);
    tomorrowDate.setDate(tomorrowDate.getDate() + 1);

    if (dateValue.getTime() === todayDate.getTime()) {
      return "Aujourd'hui";
    }
    if (dateValue.getTime() === tomorrowDate.getTime()) {
      return "Demain";
    }

    const endOfWeek = new Date(todayDate);
    endOfWeek.setDate(endOfWeek.getDate() + (7 - endOfWeek.getDay()));
    if (dateValue <= endOfWeek && dateValue > todayDate) {
      const dayNames = ["Dim", "Lun", "Mar", "Mer", "Jeu", "Ven", "Sam"];
      return dayNames[dateValue.getDay()];
    }

    const day = dateValue.getDate().toString().padStart(2, "0");
    const month = (dateValue.getMonth() + 1).toString().padStart(2, "0");

    if (dateValue.getFullYear() === now.getFullYear()) {
      return `${day}.${month}`;
    }
    return `${day}.${month}.${dateValue.getFullYear()}`;
  }

  get dateTagPreset(): TagPreset {
    const icon = this.dateMode === "range" ? "evaCalendarX" : "heroCalendar";
    return { label: this.displayLabel, severity: "secondary", icon };
  }

  /** Nom du mois affiché */
  get monthLabel(): string {
    const monthNames = [
      "Janvier",
      "Février",
      "Mars",
      "Avril",
      "Mai",
      "Juin",
      "Juillet",
      "Août",
      "Septembre",
      "Octobre",
      "Novembre",
      "Décembre",
    ];
    return `${monthNames[this.currentMonth.getMonth()]} ${this.currentMonth.getFullYear()}`;
  }

  /** Toggle le popover */
  toggle(): void {
    if (this.disabled) return;
    this.isOpen = !this.isOpen;
    if (this.isOpen) {
      // Sans date, on ouvre directement en échéance : un clic sur un jour suffit
      if (this.dateMode === "none") this.dateMode = "deadline";
      this.currentMonth = this.date
        ? new Date(this.date)
        : this.plannedStartDate
          ? new Date(this.plannedStartDate)
          : new Date();
      this.currentMonth.setDate(1);
      this.typedDate = "";
      this.typedDateError = "";
      this.buildCalendar();
      this.revealPopover();
    }
  }

  /**
   * Popover en position fixe pour passer au-dessus des panneaux qui coupent leur contenu (onglet Suivi) :
   * sous le déclencheur, ou au-dessus quand la place manque en bas.
   */
  private revealPopover(): void {
    setTimeout(() => {
      this.positionPopover();
      // Curseur dans la saisie : on peut taper la date sans toucher la souris
      this.elementRef.nativeElement.querySelector(".task-date-picker__typed input")?.focus();
      document.addEventListener("scroll", this.onAncestorScroll, true);
    });
  }

  private positionPopover(): void {
    const host: HTMLElement = this.elementRef.nativeElement;
    const popover = host.querySelector<HTMLElement>(".task-date-picker__popover");
    if (!popover) return;
    placeFixedPopover(host, popover);
  }

  /** Suit le déclencheur quand un panneau parent défile */
  private onAncestorScroll = (event: Event): void => {
    if (this.elementRef.nativeElement.contains(event.target)) return;
    this.positionPopover();
  };

  /** Ferme le popover */
  close(): void {
    this.isOpen = false;
    document.removeEventListener("scroll", this.onAncestorScroll, true);
  }

  @HostListener("window:resize")
  onResize(): void {
    if (this.isOpen) this.positionPopover();
  }

  ngOnDestroy(): void {
    document.removeEventListener("scroll", this.onAncestorScroll, true);
  }

  /**
   * Interrupteur « Date de fin » : la date déjà choisie devient le début de la période,
   * puis on choisit la fin. À l'inverse, on garde le début comme échéance.
   */
  onEndDateToggle(withEnd: boolean): void {
    if (withEnd) {
      const start = this.plannedStartDate ?? this.date;
      this.plannedStartDate = start;
      this.date = null;
      this.rangeStep = start ? "end" : "start";
      this.setMode("range");
    } else {
      this.date = this.plannedStartDate ?? this.date;
      this.plannedStartDate = null;
      this.rangeStep = "start";
      this.setMode("deadline");
    }
    this.plannedStartDateChange.emit(this.plannedStartDate);
    this.dateChange.emit(this.date);
    this.buildCalendar();
  }

  private setMode(mode: "deadline" | "range"): void {
    this.dateMode = mode;
    this.dateModeChange.emit(mode);
  }

  /** Efface les dates */
  clear(event?: Event): void {
    // La croix d'eva-tag émet sans événement (et stoppe déjà la propagation)
    event?.stopPropagation();
    this.date = null;
    this.plannedStartDate = null;
    this.dateMode = "none";
    this.rangeStep = "start";
    this.dateChange.emit(null);
    this.plannedStartDateChange.emit(null);
    this.dateModeChange.emit("none");
    this.close();
  }

  /** Sélectionne aujourd'hui */
  selectToday(): void {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    this.selectDate(today);
  }

  /** Sélectionne demain */
  selectTomorrow(): void {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);
    this.selectDate(tomorrow);
  }

  /** Sélectionne le lundi de la semaine prochaine */
  selectNextWeek(): void {
    const nextWeek = new Date();
    const daysUntilMonday = (8 - nextWeek.getDay()) % 7 || 7;
    nextWeek.setDate(nextWeek.getDate() + daysUntilMonday);
    nextWeek.setHours(0, 0, 0, 0);
    this.selectDate(nextWeek);
  }

  /** Entrée dans le champ de saisie : applique la date tapée comme un clic sur ce jour */
  applyTypedDate(event: Event): void {
    // Ne pas soumettre le formulaire de tâche autour
    event.preventDefault();
    event.stopPropagation();
    const date = parseTypedDate(this.typedDate);
    if (!date) {
      this.typedDateError = "Date non reconnue (ex. 02.11.2026)";
      return;
    }
    if (this.minDate && date < this.minDate) {
      this.typedDateError = "Date trop ancienne";
      return;
    }
    this.typedDate = "";
    this.typedDateError = "";
    this.currentMonth = new Date(date.getFullYear(), date.getMonth(), 1);
    this.selectDate(date);
  }

  onTypedDateChange(value: string): void {
    this.typedDate = value;
    this.typedDateError = "";
  }

  /** Sélectionne une date du calendrier */
  selectCalendarDay(day: CalendarDay): void {
    if (day.isDisabled) return;
    this.selectDate(day.date);
  }

  /** Sélection commune — gère les modes deadline et range */
  private selectDate(date: Date): void {
    if (this.dateMode === "range") {
      if (this.rangeStep === "start") {
        this.plannedStartDate = date;
        this.plannedStartDateChange.emit(date);
        // Si la fin actuelle est antérieure au nouveau début, l'effacer
        if (this.date && this.date < date) {
          this.date = null;
          this.dateChange.emit(null);
        }
        this.rangeStep = "end";
        this.buildCalendar();
        return;
      } else {
        // Si on sélectionne une date avant le début → inverser
        if (this.plannedStartDate && date < this.plannedStartDate) {
          const oldStart = this.plannedStartDate;
          this.plannedStartDate = date;
          this.plannedStartDateChange.emit(date);
          this.date = oldStart;
          this.dateChange.emit(oldStart);
        } else {
          this.date = date;
          this.dateChange.emit(date);
        }
        this.close();
        this.focusTrigger();
        return;
      }
    }
    // Mode deadline
    this.setMode("deadline");
    this.date = date;
    this.dateChange.emit(date);
    this.close();
    this.focusTrigger();
  }

  /** Entrée ou Espace sur la pastille de date l'ouvrent (pas sur sa croix) */
  onTagKeyDown(event: KeyboardEvent): void {
    if (event.target !== event.currentTarget || (event.key !== "Enter" && event.key !== " ")) return;
    event.preventDefault();
    this.toggle();
  }

  /** Après un choix, le focus revient sur la pastille de date pour enchaîner au clavier (Tab) */
  private focusTrigger(): void {
    setTimeout(() => this.elementRef.nativeElement.querySelector("eva-tag, button")?.focus());
  }

  /** Tab hors du calendrier : le refermer */
  @HostListener("focusout", ["$event"])
  onFocusOut(event: FocusEvent): void {
    const next = event.relatedTarget as Node | null;
    if (this.isOpen && next && !this.elementRef.nativeElement.contains(next)) this.close();
  }

  /** Mois précédent */
  prevMonth(): void {
    this.currentMonth = new Date(this.currentMonth.getFullYear(), this.currentMonth.getMonth() - 1, 1);
    this.buildCalendar();
  }

  /** Mois suivant */
  nextMonth(): void {
    this.currentMonth = new Date(this.currentMonth.getFullYear(), this.currentMonth.getMonth() + 1, 1);
    this.buildCalendar();
  }

  /** Construit la grille du calendrier */
  private buildCalendar(): void {
    this.calendarDays = [];

    const year = this.currentMonth.getFullYear();
    const month = this.currentMonth.getMonth();

    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);

    let startDay = firstDay.getDay() - 1;
    if (startDay < 0) startDay = 6;

    const prevMonthLastDay = new Date(year, month, 0).getDate();
    for (let i = startDay - 1; i >= 0; i--) {
      const date = new Date(year, month - 1, prevMonthLastDay - i);
      this.calendarDays.push(this.createCalendarDay(date, false));
    }

    for (let i = 1; i <= lastDay.getDate(); i++) {
      const date = new Date(year, month, i);
      this.calendarDays.push(this.createCalendarDay(date, true));
    }

    const remaining = 42 - this.calendarDays.length;
    for (let i = 1; i <= remaining; i++) {
      const date = new Date(year, month + 1, i);
      this.calendarDays.push(this.createCalendarDay(date, false));
    }
  }

  /** Crée un objet CalendarDay */
  private createCalendarDay(date: Date, isCurrentMonth: boolean): CalendarDay {
    const dateValue = new Date(date);
    dateValue.setHours(0, 0, 0, 0);

    const selectedDate = this.date ? new Date(this.date) : null;
    if (selectedDate) selectedDate.setHours(0, 0, 0, 0);

    const startDate = this.plannedStartDate ? new Date(this.plannedStartDate) : null;
    if (startDate) startDate.setHours(0, 0, 0, 0);

    const isDisabled = this.minDate ? dateValue < this.minDate : false;

    const isStart = this.dateMode === "range" && startDate ? dateValue.getTime() === startDate.getTime() : false;
    const isEnd = selectedDate ? dateValue.getTime() === selectedDate.getTime() : false;
    const isInRange =
      this.dateMode === "range" && startDate && selectedDate
        ? dateValue > startDate && dateValue < selectedDate
        : false;

    const isSelected =
      this.dateMode === "range"
        ? isStart || isEnd
        : selectedDate
          ? dateValue.getTime() === selectedDate.getTime()
          : false;

    return {
      date: dateValue,
      day: dateValue.getDate(),
      isCurrentMonth,
      isToday: dateValue.getTime() === this.today.getTime(),
      isSelected,
      isDisabled,
      isStart,
      isEnd,
      isInRange,
    };
  }

  /** Format court pour l'affichage range (dd.MM) */
  private formatShortDate(date: Date): string {
    const d = new Date(date);
    const day = d.getDate().toString().padStart(2, "0");
    const month = (d.getMonth() + 1).toString().padStart(2, "0");
    return `${day}.${month}`;
  }

  /** Ferme si clic à l'extérieur */
  onDocumentClick(event: Event): void {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.close();
    }
  }

  /** Échap depuis le popover : le fermer seul, sans fermer le panneau ou la modale autour */
  @HostListener("keydown.escape", ["$event"])
  onHostEscape(event: KeyboardEvent): void {
    if (!this.isOpen || event.defaultPrevented) return;
    event.preventDefault();
    this.close();
  }

  /** Ferme sur Escape */
  @HostListener("document:keydown.escape")
  onEscape(): void {
    this.close();
  }
}
